import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { XIcon, CheckCircleIcon, ChevronUpIcon, ChevronDownIcon, UploadIcon, AlertTriangleIcon, EyeIcon, Trash2Icon } from 'lucide-react';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason } from '../types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: Date;
}
export function ChallengeDispute() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review' | 'submitted'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [orderUploadedFiles, setOrderUploadedFiles] = useState<UploadedFile[]>([]);
  const [customerUploadedFiles, setCustomerUploadedFiles] = useState<UploadedFile[]>([]);
  const [otherUploadedFiles, setOtherUploadedFiles] = useState<UploadedFile[]>([]);
  // Use centralized dispute generation
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    console.log('Looking for dispute ID:', id);
    console.log('Total disputes generated:', allDisputes.length);
    console.log('First 10 dispute IDs:', allDisputes.slice(0, 10).map(d => d.id));
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      console.log('Dispute not found, using fallback (dispute 1)');
      return allDisputes[0];
    }
    console.log('Found dispute:', found.id, 'Card Brand:', found.cardBrand);
    return found;
  }, [id, allDisputes]);
  // Get evidence recommendations based on card brand and reason code
  const evidenceRecs = useMemo(() => {
    console.group('🔍 ChallengeDispute - Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand:', dispute.cardBrand);
    console.log('Network Reason Code:', dispute.networkReasonCode);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Debug log to verify card brand
    console.log('Dispute ID:', dispute.id, 'Card Brand:', dispute.cardBrand, 'Lowercase:', cardBrand);
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  const shippingAddress = 'Rabbit Gateway Office, 123 Silom Road, Bang Rak, Bangkok 10500, Thailand';
  const billingAddress = 'Rabbit Gateway Office, 123 Silom Road, Bang Rak, Bangkok 10500, Thailand';
  const formatCurrency = (amount: number, currency: string) => {
    return `THB ${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  const simulateFileUpload = (step: 'evidence' | 'order' | 'customer' | 'other') => {
    const mockFile: UploadedFile = {
      id: Math.random().toString(36).substr(2, 9),
      name: 'OHMM-Cairo-Specs-She...9Jul25.pdf',
      size: 1040000,
      uploadedAt: new Date()
    };
    switch (step) {
      case 'evidence':
        setUploadedFiles([...uploadedFiles, mockFile]);
        break;
      case 'order':
        setOrderUploadedFiles([...orderUploadedFiles, mockFile]);
        break;
      case 'customer':
        setCustomerUploadedFiles([...customerUploadedFiles, mockFile]);
        break;
      case 'other':
        setOtherUploadedFiles([...otherUploadedFiles, mockFile]);
        break;
    }
  };
  const removeFile = (fileId: string, step: 'evidence' | 'order' | 'customer' | 'other') => {
    switch (step) {
      case 'evidence':
        setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'order':
        setOrderUploadedFiles(orderUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'customer':
        setCustomerUploadedFiles(customerUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'other':
        setOtherUploadedFiles(otherUploadedFiles.filter(f => f.id !== fileId));
        break;
    }
  };
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleSubmit = () => {
    setChallengeStep('submitted');
  };
  const handleClose = () => {
    navigate(`/disputes/${id}`);
  };
  const handleViewAllDisputes = () => {
    navigate('/disputes');
  };
  const handleBackToDisputeDetails = () => {
    navigate(`/disputes/${id}`, {
      state: {
        challenged: true
      }
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-6753">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-6754">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6755">
          <div className="flex items-center justify-between h-16" data-id="element-6756">
            <h1 className="text-lg font-semibold text-gray-900" data-id="element-6757">
              Challenge dispute
            </h1>
            <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-6758">
              <XIcon size={20} data-id="element-6759" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-6760">
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-6761">
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-64 flex-shrink-0" data-id="element-6762">
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2 sticky top-24" data-id="element-6763">
              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 text-gray-500" onClick={() => toggleSection('challengeDetails')} data-id="element-6764">
                <span data-id="element-6765">Challenge details</span>
                {expandedSections.challengeDetails ? <ChevronUpIcon size={16} className="ml-auto" data-id="element-6766" /> : <ChevronDownIcon size={16} className="ml-auto" data-id="element-6767" />}
              </button>
              {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-6768">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-6769">
                    {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-6770" />}
                    Challenge reason
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-6771">
                    {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-6772" />}
                    Supporting evidence
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-6773">
                <span data-id="element-6774">Transaction details</span>
                {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-6775" /> : <ChevronDownIcon size={16} data-id="element-6776" />}
              </button>
              {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-6777">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-6778">
                    {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-6779" />}
                    Order details
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-6780">
                    {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-6781" />}
                    Customer details
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-6782">
                <span data-id="element-6783">Other evidence</span>
                {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-6784" /> : <ChevronDownIcon size={16} data-id="element-6785" />}
              </button>
              {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-6786">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-6787">
                    {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-6788" />}
                    Other supporting documents
                  </button>
                </div>}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1" data-id="element-6789">
            {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-6790">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-6791">
                  <div data-id="element-6792">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6793">
                      Challenge reason
                    </h1>
                    <p className="text-gray-600" data-id="element-6794">
                      Your responses will help us recommend the best evidence to
                      submit for your challenge.
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-6795">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-6796">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6797">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-6798">
                      <div className="text-gray-500" data-id="element-6799">Dispute amount</div>
                      <div className="font-medium" data-id="element-6800">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-6801">
                      <div className="text-gray-500" data-id="element-6802">Response due</div>
                      <div className="font-medium" data-id="element-6803">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-6804">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-6805">
                  <div data-id="element-6806">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-6807">
                      Why are you challenging the dispute?
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-6808">
                      Choose the closest option.
                    </p>
                    <div className="space-y-3" data-id="element-6809">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6810">
                        <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-6811" />
                        <span className="text-base text-gray-900" data-id="element-6812">
                          The customer withdrew the dispute
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6813">
                        <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-6814" />
                        <span className="text-base text-gray-900" data-id="element-6815">
                          The customer has already been refunded
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6816">
                        <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-6817" />
                        <span className="text-base text-gray-900" data-id="element-6818">
                          The customer has a purchasing history with me
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6819">
                        <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-6820" />
                        <span className="text-base text-gray-900" data-id="element-6821">
                          Other reasons
                        </span>
                      </label>
                    </div>
                  </div>

                  <div data-id="element-6822">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-6823">
                      What type of product/service was the disputed payment for?
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-6824">
                      Choose the closest option.
                    </p>
                    <div className="space-y-3" data-id="element-6825">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6826">
                        <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-6827" />
                        <div className="flex-1" data-id="element-6828">
                          <div className="text-base text-gray-900 font-medium" data-id="element-6829">
                            Physical product
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-6830">
                            Physical goods to be delivered to the customer, e.g.
                            clothes, accessories, electronic gadgets
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6831">
                        <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-6832" />
                        <div className="flex-1" data-id="element-6833">
                          <div className="text-base text-gray-900 font-medium" data-id="element-6834">
                            Digital product or service
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-6835">
                            Purchased access to software, digital files or
                            experiences, e.g. mobile apps, music service,
                            digital tickets
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6836">
                        <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-6837" />
                        <div className="flex-1" data-id="element-6838">
                          <div className="text-base text-gray-900 font-medium" data-id="element-6839">
                            Offline service
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-6840">
                            Labour or assistant services provided either at a
                            specific location or remotely, e.g. transportation,
                            home servicing
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6841">
                        <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-6842" />
                        <div className="flex-1" data-id="element-6843">
                          <div className="text-base text-gray-900 font-medium" data-id="element-6844">
                            Reservation or booking
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-6845">
                            Services related to reservations or bookings, e.g.
                            flight tickets, hotel booking
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-6846">
                        <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-6847" />
                        <span className="text-base text-gray-900" data-id="element-6848">Other</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6849">
                  <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6850">
                    Cancel
                  </button>
                  <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300" data-id="element-6851">
                    Next
                  </button>
                </div>
              </div>}

            {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-6852">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-6853">
                  <div data-id="element-6854">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6855">
                      {evidenceRecs.title}
                    </h1>
                    <p className="text-gray-600" data-id="element-6856">
                      {evidenceRecs.description}
                      {evidenceRecs.learnMoreLink && <>
                          {' '}
                          <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-6857">
                            {evidenceRecs.learnMoreLink}
                          </a>
                        </>}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-6858">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-6859">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6860">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-6861">
                      <div className="text-gray-500" data-id="element-6862">Dispute amount</div>
                      <div className="font-medium" data-id="element-6863">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-6864">
                      <div className="text-gray-500" data-id="element-6865">Response due</div>
                      <div className="font-medium" data-id="element-6866">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-6867">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-6868">
                  <div data-id="element-6869">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6870">
                      Recommended evidence
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-6871">
                      Based on your challenge reason, we recommend providing:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-6872">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-6873">
                          <strong data-id="element-6874">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                  </div>

                  <div data-id="element-6875">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6876">
                      Provide supporting evidence
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-6877">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-6878" />
                            {requirements.language}
                            <br data-id="element-6879" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {uploadedFiles.length > 0 && <div className="space-y-3" data-id="element-6880">
                      {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-6881">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-6882">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6883">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6884" />
                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-6885" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-6886">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-6887">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-6888">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-6889">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-6890">
                              <EyeIcon size={20} data-id="element-6891" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'evidence')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-6892">
                              <Trash2Icon size={20} data-id="element-6893" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-6894">
                    <button disabled={noEvidenceChecked} onClick={() => simulateFileUpload('evidence')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6895">
                      <UploadIcon size={16} data-id="element-6896" />
                      Upload file
                    </button>
                  </div>

                  <div data-id="element-6897">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6898" />
                  </div>

                  <div data-id="element-6899">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-6900">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6901" />
                      <span className="text-sm text-gray-900" data-id="element-6902">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6903">
                  <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6904">
                    Back
                  </button>
                  <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6905">
                    Next
                  </button>
                </div>
              </div>}

            {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-6906">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-6907">
                  <div data-id="element-6908">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6909">
                      Order details
                    </h1>
                    <p className="text-gray-600" data-id="element-6910">
                      Please provide as much information as possible about the
                      goods/service the payment was disputed for. This will
                      allow the issuer to verify that the evidence you provided
                      corresponds to the transaction disputed by the customer.
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-6911">
                      The details below have been pre-filled from transaction
                      data. Please review and edit the information if needed.
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-6912">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-6913">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6914">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-6915">
                      <div className="text-gray-500" data-id="element-6916">Dispute amount</div>
                      <div className="font-medium" data-id="element-6917">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-6918">
                      <div className="text-gray-500" data-id="element-6919">Response due</div>
                      <div className="font-medium" data-id="element-6920">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-6921">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-6922">
                  <div data-id="element-6923">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6924">
                      Product details
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6925">
                      <span data-id="element-6926">
                        IPHONE7 * 5,{' '}
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6927">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6928">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6929" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6930">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6931">
                      Shipping address
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6932">
                      <span data-id="element-6933">{shippingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6934">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6935">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6936" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6937">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6938">
                      Shipping carrier
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6939">
                      <span data-id="element-6940">sameday</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6941">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6942">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6943" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6944">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-6945">
                      Provide additional supporting evidence
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-6946">
                      As the payment was for a{' '}
                      <span className="font-medium" data-id="element-6947">Physical product</span>, we
                      recommend you to also provide the following information:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-6948">
                      <li data-id="element-6949">Shipping date</li>
                      <li data-id="element-6950">Shipping tracking number</li>
                    </ul>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-6951">
                      You can upload any document containing such information
                      (e.g. invoices, shipping documentation, delivery
                      confirmation or shipping receipts) below.
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-6952">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-6953" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-6954" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  <div data-id="element-6955">
                    <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-6956">
                      <UploadIcon size={16} data-id="element-6957" />
                      Upload file
                    </button>
                  </div>

                  <div data-id="element-6958">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-6959" />
                  </div>

                  <div data-id="element-6960">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-6961">
                      <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-6962" />
                      <span className="text-sm text-gray-900" data-id="element-6963">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-6964">
                  <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-6965">
                    Back
                  </button>
                  <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-6966">
                    Next
                  </button>
                </div>
              </div>}

            {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-6967">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-6968">
                  <div data-id="element-6969">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-6970">
                      Customer details
                    </h1>
                    <p className="text-gray-600" data-id="element-6971">
                      Please provide as much information as possible about the
                      customer. This will allow the issuer to verify that the
                      evidence you provided corresponds to the customer
                      disputing the transaction.
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-6972">
                      The details below have been pre-filled from transaction
                      data. Please review and edit the information if needed.
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-6973">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-6974">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-6975">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-6976">
                      <div className="text-gray-500" data-id="element-6977">Dispute amount</div>
                      <div className="font-medium" data-id="element-6978">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-6979">
                      <div className="text-gray-500" data-id="element-6980">Response due</div>
                      <div className="font-medium" data-id="element-6981">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-6982">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-6983">
                  <div data-id="element-6984">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6985">
                      Customer name
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6986">
                      <span data-id="element-6987">Test name</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6988">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6989">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6990" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6991">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6992">
                      Customer email
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-6993">
                      <span data-id="element-6994">customer@example.com</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-6995">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6996">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6997" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-6998">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-6999">
                      Customer purchase IP
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7000">
                      <span data-id="element-7001">35.221.170.121</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-7002">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7003">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7004" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-7005">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7006">
                      Customer billing address
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7007">
                      <span data-id="element-7008">{billingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-7009">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7010">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7011" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-7012">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-7013">
                      Provide supporting evidence
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-7014">
                      We recommend you to provide the following information
                      about the customer:
                    </p>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-7015">
                      You can upload any document containing such information
                      (e.g. invoices, receipts) below.
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-7016">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-7017" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-7018" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {customerUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-7019">
                      {customerUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-7020">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-7021">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7022">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7023" />
                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7024" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-7025">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-7026">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-7027">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-7028">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-7029">
                              <EyeIcon size={20} data-id="element-7030" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'customer')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-7031">
                              <Trash2Icon size={20} data-id="element-7032" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-7033">
                    <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-7034">
                      <UploadIcon size={16} data-id="element-7035" />
                      Upload file
                    </button>
                  </div>

                  <div data-id="element-7036">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-7037" />
                  </div>

                  <div data-id="element-7038">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-7039">
                      <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-7040" />
                      <span className="text-sm text-gray-900" data-id="element-7041">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7042">
                  <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7043">
                    Back
                  </button>
                  <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-7044">
                    Next
                  </button>
                </div>
              </div>}

            {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-7045">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-7046">
                  <div data-id="element-7047">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7048">
                      Other supporting documents
                    </h1>
                    <p className="text-gray-600" data-id="element-7049">
                      If you have any other document(s) that you believe is
                      valuable to assess the legitimacy of your challenge, you
                      can add it below.
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-7050">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-7051">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7052">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-7053">
                      <div className="text-gray-500" data-id="element-7054">Dispute amount</div>
                      <div className="font-medium" data-id="element-7055">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-7056">
                      <div className="text-gray-500" data-id="element-7057">Response due</div>
                      <div className="font-medium" data-id="element-7058">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-7059">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-7060">
                  <div data-id="element-7061">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-7062">
                      Provide supporting evidence
                    </h3>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-7063">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-7064" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-7065" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {otherUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-7066">
                      {otherUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-7067">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-7068">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7069">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7070" />
                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7071" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-7072">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-7073">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-7074">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-7075">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-7076">
                              <EyeIcon size={20} data-id="element-7077" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'other')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-7078">
                              <Trash2Icon size={20} data-id="element-7079" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-7080">
                    <button disabled={noOtherEvidenceChecked} onClick={() => simulateFileUpload('other')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-7081">
                      <UploadIcon size={16} data-id="element-7082" />
                      Upload file
                    </button>
                  </div>

                  <div data-id="element-7083">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-7084" />
                  </div>

                  <div data-id="element-7085">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-7086">
                      <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-7087" />
                      <span className="text-sm text-gray-900" data-id="element-7088">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7089">
                  <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7090">
                    Back
                  </button>
                  <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-7091">
                    Review
                  </button>
                </div>
              </div>}

            {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-7092">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-7093">
                  <div data-id="element-7094">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7095">
                      Review your challenge
                    </h1>
                    <p className="text-gray-600" data-id="element-7096">
                      Please confirm that the information you have provided is
                      correct before submission.
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-7097">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-7098">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7099">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-7100">
                      <div className="text-gray-500" data-id="element-7101">Dispute amount</div>
                      <div className="font-medium" data-id="element-7102">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-7103">
                      <div className="text-gray-500" data-id="element-7104">Response due</div>
                      <div className="font-medium" data-id="element-7105">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-7106">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-7107">
                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-7108">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-7109">
                      Challenge details
                    </h3>
                    <div className="space-y-3" data-id="element-7110">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-7111">
                        <span className="text-sm text-gray-600" data-id="element-7112">
                          Reason for challenge
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-7113">
                          The customer has a purchasing history with me
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-7114">
                        <span className="text-sm text-gray-600" data-id="element-7115">
                          Type of product or service
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-7116">
                          Physical product
                        </span>
                      </div>
                      {uploadedFiles.length > 0 && <div className="flex flex-col gap-2" data-id="element-7117">
                          <span className="text-sm text-gray-600" data-id="element-7118">
                            Supporting evidence
                          </span>
                          <div className="space-y-2" data-id="element-7119">
                            {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-2 text-sm" data-id="element-7120">
                                <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-7121">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7122">
                                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7123" />
                                    <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7124" />
                                  </svg>
                                </div>
                                <div className="flex-1" data-id="element-7125">
                                  <div className="text-gray-900 font-medium" data-id="element-7126">
                                    {file.name}
                                  </div>
                                  <div className="text-gray-500" data-id="element-7127">
                                    {formatFileSize(file.size)}
                                  </div>
                                </div>
                                <button className="text-blue-600 hover:text-blue-700" data-id="element-7128">
                                  <EyeIcon size={16} data-id="element-7129" />
                                </button>
                              </div>)}
                          </div>
                        </div>}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-7130">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-7131">
                      Transaction details
                    </h3>
                    <div className="space-y-3" data-id="element-7132">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-7133">
                        <span className="text-sm text-gray-600" data-id="element-7134">
                          Product details
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-7135">
                          IPHONE7 * 5,{' '}
                          {formatCurrency(dispute.amount, dispute.currency)}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-7136">
                        <span className="text-sm text-gray-600" data-id="element-7137">
                          Shipping address
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-7138">
                          {shippingAddress}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-7139">
                        <span className="text-sm text-gray-600" data-id="element-7140">
                          Customer name
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-7141">
                          Test name
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-7142">
                        <span className="text-sm text-gray-600" data-id="element-7143">
                          Customer email
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-7144">
                          customer@example.com
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-7145">
                        <span className="text-sm text-gray-600" data-id="element-7146">
                          Customer purchase IP
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-7147">
                          35.221.170.121
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-7148">
                        <span className="text-sm text-gray-600" data-id="element-7149">
                          Customer billing address
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-7150">
                          {billingAddress}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-7151">
                      <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-7152" />
                      <p className="text-sm text-gray-900" data-id="element-7153">
                        Please attach at least one of the recommended evidence
                        types to proceed with the response.
                      </p>
                    </div>}

                  <div data-id="element-7154">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-7155">
                      <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-7156" />
                      <span className="text-sm text-gray-900" data-id="element-7157">
                        I understand that the current evidence can only be
                        submitted once for the issuer's review, and cannot be
                        reused.
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7158">
                  <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7159">
                    Back
                  </button>
                  <button onClick={handleSubmit} disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-7160">
                    Submit
                  </button>
                </div>
              </div>}

            {challengeStep === 'submitted' && <div className="max-w-3xl mx-auto py-12" data-id="element-7161">
                <div className="text-center mb-8" data-id="element-7162">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-100 mb-6" data-id="element-7163">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7164">
                      <path d="M24 4L6 14L24 24L42 14L24 4Z" fill="#FF6B35" stroke="#FF6B35" strokeWidth="2" strokeLinejoin="round" data-id="element-7165" />
                      <path d="M6 24L24 34L42 24" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7166" />
                      <path d="M6 34L24 44L42 34" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7167" />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-7168">
                    Your dispute challenge has been submitted.
                  </h1>
                  <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-7169">
                    Thank you for submitting your information. The issuing bank
                    will review your challenge and make a decision within 115
                    days. We will notify you on any updates along the process.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-8 space-y-8" data-id="element-7170">
                  <div data-id="element-7171">
                    <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-7172">
                      Dispute challenge
                    </h2>

                    <div className="space-y-6" data-id="element-7173">
                      <div data-id="element-7174">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-7175">
                          Challenge details
                        </h3>
                        <div className="space-y-3" data-id="element-7176">
                          <div className="flex" data-id="element-7177">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7178">
                              Reason for challenge
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-7179">
                              The customer has a purchasing history with me
                            </span>
                          </div>
                          <div className="flex" data-id="element-7180">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7181">
                              Type of product or service
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-7182">
                              Physical product
                            </span>
                          </div>
                          <div className="flex" data-id="element-7183">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7184">
                              Supporting evidence
                            </span>
                            <div className="flex-1" data-id="element-7185">
                              {uploadedFiles.length > 0 ? uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 mb-2" data-id="element-7186">
                                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-7187">
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7188">
                                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7189" />
                                        <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7190" />
                                      </svg>
                                    </div>
                                    <div className="flex-1" data-id="element-7191">
                                      <div className="text-sm font-medium text-gray-900" data-id="element-7192">
                                        {file.name}
                                      </div>
                                      <div className="text-sm text-gray-500" data-id="element-7193">
                                        {formatFileSize(file.size)}
                                      </div>
                                    </div>
                                    <button className="text-blue-600 hover:text-blue-700" data-id="element-7194">
                                      <EyeIcon size={18} data-id="element-7195" />
                                    </button>
                                  </div>) : <span className="text-sm text-gray-900" data-id="element-7196">
                                  No files uploaded
                                </span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-7197">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-7198">
                          Transaction details
                        </h3>
                        <div className="space-y-3" data-id="element-7199">
                          <div className="flex" data-id="element-7200">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7201">
                              Product details
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-7202">
                              IPHONE7 * 5,{' '}
                              {formatCurrency(dispute.amount, dispute.currency)}
                            </span>
                          </div>
                          <div className="flex" data-id="element-7203">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7204">
                              Shipping address
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-7205">
                              {shippingAddress}
                            </span>
                          </div>
                          <div className="flex" data-id="element-7206">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7207">
                              Customer name
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-7208">
                              Test name
                            </span>
                          </div>
                          <div className="flex" data-id="element-7209">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7210">
                              Customer email
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-7211">
                              customer@example.com
                            </span>
                          </div>
                          <div className="flex" data-id="element-7212">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7213">
                              Customer purchase IP
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-7214">
                              35.221.170.121
                            </span>
                          </div>
                          <div className="flex" data-id="element-7215">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-7216">
                              Customer billing address
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-7217">
                              {billingAddress}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3" data-id="element-7218">
                    <button onClick={handleViewAllDisputes} className="px-6 py-3 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50" data-id="element-7219">
                      View all Disputes
                    </button>
                    <button onClick={handleBackToDisputeDetails} className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-7220">
                      Back to Dispute details
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* No Evidence Warning Modal */}
      {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-7221">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-7222">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-7223">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-7224"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-7225">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-7226">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-7227">
                <div className="flex items-start gap-4" data-id="element-7228">
                  <div className="flex-shrink-0" data-id="element-7229">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-7230">
                      <AlertTriangleIcon size={24} className="text-red-600" data-id="element-7231" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-7232">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-7233">
                      Are you sure you want to proceed without evidence?
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-7234">
                      Submitting a challenge without supporting evidence
                      significantly reduces your chances of winning the dispute.
                      We strongly recommend providing at least one type of
                      evidence to support your case.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3" data-id="element-7235">
                <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 order-2 sm:order-1" data-id="element-7236">
                  Go back and add evidence
                </button>
                <button onClick={() => {
              setShowNoEvidenceWarning(false);
              setChallengeStep('review');
            }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 order-1 sm:order-2" data-id="element-7237">
                  Proceed anyway
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}